import React from 'react';
import { Box, Divider, Drawer, List, ListItem, ListItemIcon, ListItemText, Typography, Avatar } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SavingsIcon from '@mui/icons-material/Savings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HelpIcon from '@mui/icons-material/Help';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ExploreIcon from '@mui/icons-material/Explore';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ForumIcon from '@mui/icons-material/Forum';
import HomeIcon from '@mui/icons-material/Home';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CampaignIcon from '@mui/icons-material/Campaign';
import FlagIcon from '@mui/icons-material/Flag';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SmsIcon from '@mui/icons-material/Sms';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../styles';
import { capturePageContext } from '../services/githubService';
import { isAdminMode, getMainAppUrl, getAdminUrl } from '../utils/host';

const drawerWidth = 240;
const adminDrawerWidth = 268;

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const adminMode = isAdminMode();
  const resolvedDrawerWidth = adminMode ? adminDrawerWidth : drawerWidth;

  // Admin status comes from the user payload on /auth/verify
  const isAdmin = !adminMode && user?.isAdmin === true;

  const mainMenuItems = [
    { text: 'Activity', icon: <AccessTimeIcon />, path: '/activity' },
    { text: 'Budgets', icon: <SavingsIcon />, path: '/budgets' },
    { text: 'Money', icon: <AccountBalanceIcon />, path: '/money' },
    { text: 'Bills', icon: <ReceiptLongIcon />, path: '/bills' },
    { text: 'Goals', icon: <FlagIcon />, path: '/goals' },
    { text: 'Discover', icon: <ExploreIcon />, path: '/discover' },
  ];

  const topMenuGroups = adminMode ? [
    {
      label: 'Operate',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'Users', icon: <PeopleIcon />, path: '/users' },
        { text: 'Alerts', icon: <NotificationsActiveIcon />, path: '/alerts' },
        { text: 'Campaigns', icon: <MailOutlineIcon />, path: '/campaigns' },
      ]
    },
    {
      label: 'Growth',
      items: [
        { text: 'Discover', icon: <ForumIcon />, path: '/discover' },
        { text: 'Contest', icon: <EmojiEventsIcon />, path: '/contest' },
        { text: 'Social', icon: <CampaignIcon />, path: '/social' },
        { text: 'Feature Adoption', icon: <RocketLaunchIcon />, path: '/feature-adoption' },
        { text: 'Communications', icon: <SmsIcon />, path: '/communications' },
      ]
    }
  ] : [{ label: null, items: mainMenuItems }];

  const bottomMenuItems = [
    // In admin mode, show link to main app. In main app, show Admin link if user is admin
    ...(adminMode ? [{
      text: 'Main App',
      icon: <HomeIcon />,
      external: true,
      onClick: () => {
        window.location.href = getMainAppUrl();
      }
    }] : isAdmin ? [{
      text: 'Admin',
      icon: <AdminPanelSettingsIcon />,
      external: true,
      onClick: () => {
        window.location.href = getAdminUrl();
      }
    }] : []),
    // Account and Support - in admin mode, link to main app since those routes don't exist in admin portal
    ...(adminMode ? [] : [{ 
      text: 'Account', 
      icon: user?.profileImage ? (
        <Avatar 
          src={user.profileImage} 
          sx={{ 
            width: 32, 
            height: 32,
            border: `1px solid ${colors.primary}`,
            marginLeft: '-4px'
          }}
        />
      ) : <AccountCircleIcon />, 
      path: '/account' 
    },
    { 
      text: 'Support', 
      icon: <HelpIcon />, 
      path: '/support',
      onClick: () => {
        const context = capturePageContext();
        sessionStorage.setItem('pageContext', JSON.stringify(context));
        navigate('/support');
      }
    }]),
  ];

  const ListItemStyled = ({ item }) => {
    const isActive = Boolean(item.path) && !item.external && (
      location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
    );
    const activeBg = alpha(colors.primary, adminMode ? 0.13 : 0.125);
    const hoverBg = adminMode ? alpha(colors.secondary, 0.05) : alpha('#000', 0.04);
    return (
      <ListItem
        component={item.onClick || item.external ? 'div' : Link}
        to={!item.onClick && !item.external ? item.path : undefined}
        onClick={item.onClick}
        aria-current={isActive ? 'page' : undefined}
        sx={{
          borderRadius: adminMode ? 2.5 : '0.5rem',
          my: adminMode ? 0.35 : 0.5,
          px: adminMode ? 1.5 : 2,
          py: adminMode ? 1.1 : 1,
          minHeight: adminMode ? 46 : 'auto',
          color: isActive ? colors.primary : colors.secondary,
          cursor: 'pointer',
          transition: 'transform 100ms ease, background 100ms ease, color 100ms ease',
          '&:hover': {
            backgroundColor: isActive ? activeBg : hoverBg,
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
          ...(isActive && {
            backgroundColor: activeBg,
          }),
        }}
      >
        <ListItemIcon sx={{ 
          color: 'inherit',
          minWidth: adminMode ? 38 : 44,
          '& .MuiSvgIcon-root': {
            fontSize: adminMode ? 22 : 24
          }
        }}>{item.icon}</ListItemIcon>
        <ListItemText 
          primary={item.text} 
          primaryTypographyProps={{
            fontWeight: adminMode ? 800 : 900,
            fontSize: adminMode ? '0.96rem' : '1.1rem',
            lineHeight: 1.15
          }}
        />
      </ListItem>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex',
      overflowX: 'hidden', // Prevent horizontal scrolling
      width: '100vw',
      position: 'fixed',
      height: '100vh',
      bgcolor: adminMode ? 'background.default' : undefined
    }}>
      <Drawer
        sx={{
          width: resolvedDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: resolvedDrawerWidth,
            boxSizing: 'border-box',
            backgroundColor: colors.white,
            color: colors.secondary,
            display: 'flex',
            flexDirection: 'column',
            padding: adminMode ? '20px' : '24px',
            border: 'none',
            borderRight: adminMode ? `1px solid ${alpha(colors.secondary, 0.08)}` : 'none'
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Box sx={{
          mb: adminMode ? 2.5 : 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: adminMode ? 'flex-start' : 'center',
          gap: adminMode ? 1.25 : 0
        }}>
          <Box 
            component="img"
            src="/images/budgit-logo.png"
            alt="Budgit Logo"
            sx={{ 
              width: adminMode ? 46 : 56,
              height: adminMode ? 46 : 56,
              marginRight: adminMode ? 0 : 0.5,
              objectFit: 'contain'
            }}
          />
          {adminMode ? (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: colors.primary,
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: 0
                }}
              >
                Budgit
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(colors.secondary, 0.68),
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase'
                }}
              >
                Admin Console
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="h5"
              sx={{
                color: colors.primary,
                fontWeight: 800,
                letterSpacing: 0
              }}
            >
              Budgit
            </Typography>
          )}
        </Box>
        <List sx={{ py: 0 }}>
          {topMenuGroups.map((group, groupIndex) => (
            <Box key={group.label || 'main'}>
              {group.label && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    px: 1.5,
                    pt: groupIndex === 0 ? 0.5 : 2,
                    pb: 0.75,
                    color: alpha(colors.secondary, 0.46),
                    fontWeight: 900,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase'
                  }}
                >
                  {group.label}
                </Typography>
              )}
              {group.items.map((item) => (
                <ListItemStyled key={item.text} item={item} />
              ))}
            </Box>
          ))}
        </List>
        <Box sx={{ flexGrow: 1 }} />
        {adminMode && <Divider sx={{ mb: 1.25, borderColor: alpha(colors.secondary, 0.08) }} />}
        <List sx={{ py: 0 }}>
          {bottomMenuItems.map((item) => (
            <ListItemStyled key={item.text} item={item} />
          ))}
          <ListItem
            button
            onClick={() => {
              // Handle async logout
              logout().catch(error => {
                console.error('Error during logout:', error);
              });
            }}
            sx={{
              borderRadius: adminMode ? 2.5 : '0.5rem',
              my: 0.5,
              px: adminMode ? 1.5 : 2,
              py: adminMode ? 1.1 : 1,
              color: colors.secondary,
              cursor: 'pointer',
              transition: 'transform 100ms ease, background 100ms ease',
              '&:hover': {
                backgroundColor: adminMode ? alpha(colors.secondary, 0.05) : 'rgba(0, 0, 0, 0.04)',
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            <ListItemIcon sx={{ 
              color: 'inherit',
              minWidth: adminMode ? 38 : 44
            }}><ExitToAppIcon /></ListItemIcon>
            <ListItemText 
              primary="Logout"
              primaryTypographyProps={{
                fontWeight: adminMode ? 800 : 900,
                fontSize: adminMode ? '0.96rem' : '1.1rem'
              }}
            />
          </ListItem>
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100vh',
          overflowX: 'hidden',
          overflowY: 'auto',
          bgcolor: adminMode ? 'background.default' : undefined
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
